import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import PersonRepository from '@/repositories/PersonRepository';
import { Alert } from '@/ui/Alert';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { Label } from '@/ui/Label';
import { Pill } from '@/ui/Pill';
import { Spinner } from '@/ui/Spinner';
import { Textarea } from '@/ui/Textarea';
import { Typography } from '@/ui/Typography';

import './styles.css';
import type { Props } from './types';

/**
 * Support's way back in for a member who lost their phone and recovery codes.
 * Only after verifying their identity through the usual process.
 */
const TwoFactorCard = ({ personId, name }: Props) => {
  const personRepository = PersonRepository();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const queryKey = ['personMfa', personId];

  const {
    data: status,
    isLoading,
    isError
  } = useQuery({
    queryKey,
    queryFn: () => personRepository.getMfaStatus(personId),
    enabled: !!personId
  });

  const reset = useMutation({
    mutationFn: () => personRepository.resetMfa(personId, reason.trim()),
    onSuccess: () => {
      setReason('');
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return (
    <Card padding="lg">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Typography variant="subheading" as="h3">
            Two-factor authentication
          </Typography>
          <Typography muted>
            Reset it when a member has lost both their phone and their recovery codes.
          </Typography>
        </div>
        {status && (
          <Pill variant="subtle" color={status.enabled ? 'primary' : 'neutral'} size="sm">
            {status.enabled ? 'On' : 'Off'}
          </Pill>
        )}
      </div>

      <div className="mt-5 space-y-4 border-t border-uniform-gray-200 pt-5">
        {isLoading && <Spinner size="sm" label="Loading two-factor status" />}
        {isError && <Alert variant="error" message="Couldn't load two-factor status." />}
        {reset.isSuccess && (
          <Alert
            variant="success"
            message={`Two-factor authentication is off for ${name}. They can log in with their password and turn it on again.`}
          />
        )}

        {status && !status.enabled && !reset.isSuccess && (
          <Typography>{name} doesn&apos;t have two-factor authentication on.</Typography>
        )}

        {status?.enabled && (
          <>
            <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
              {status.totp && (
                <>
                  <dt className="text-uniform-gray-600">Authenticator app</dt>
                  <dd className="text-uniform-gray-900">
                    Added {new Date(status.totp.created_at).toLocaleDateString()}
                  </dd>
                </>
              )}
              <dt className="text-uniform-gray-600">Recovery codes</dt>
              <dd className="text-uniform-gray-900">
                {status.recovery_codes_remaining} recovery codes left
              </dd>
            </dl>

            <Alert
              variant="warning"
              message="Only reset after verifying the member's identity. Resetting turns two-factor off and removes every trusted device, so the next login on any device asks only for the password. Devices already signed in stay signed in."
            />

            <div className="space-y-1.5">
              <Label htmlFor="mfa-reset-reason" text="Reason" required />
              <Textarea
                id="mfa-reset-reason"
                rows={3}
                maxLength={500}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="e.g. Lost phone, identity verified on call"
              />
            </div>

            {reset.isError && <Alert variant="error" message="Couldn't reset two-factor." />}

            <div className="flex justify-end">
              <Button
                color="destructive"
                onClick={() => reset.mutate()}
                disabled={!reason.trim()}
                isLoading={reset.isPending}>
                Reset two-factor authentication
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

export default React.memo(TwoFactorCard);
